import React from 'react'
import {
  AreaSearch,
  Bicycle,
  BrainResearch,
  ChatBubble,
  CoinsSwap,
  Compass,
  DocStarIn,
  Donate,
  Golf,
  MagicWand,
  QuoteMessage,
  Strategy,
} from 'iconoir-react'
import {
  BookmarkIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  EyeIcon,
  HeartIcon,
  LightBulbIcon,
  PlusIcon,
  PuzzlePieceIcon,
  SpeakerWaveIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  VideoCameraIcon,
  WrenchIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
  SparklesIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  BriefcaseIcon,
  BookOpenIcon,
  BanknotesIcon,
  PhoneIcon,
  RocketLaunchIcon,
  MapIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  Square3Stack3DIcon,
  ChatBubbleBottomCenterTextIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  KeyIcon,
  RectangleStackIcon,
  BuildingOffice2Icon,
  ArrowUturnRightIcon,
  ArrowUturnLeftIcon,
  TrophyIcon,
  CalendarDaysIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  TagIcon,
  EllipsisHorizontalIcon,
  FunnelIcon,
  ArrowPathIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import LinkedInIcon from '@mui/icons-material/LinkedIn'

// Map of display names to icon components
const iconMap = {
  'action-plan': Strategy,
  add: PlusIcon,
  ayla: MagicWand,
  article: Compass,
  assessment: BrainResearch,
  attachment: PaperClipIcon,
  audio: SpeakerWaveIcon,
  back: ArrowUturnRightIcon,
  bookmark: BookmarkIcon,
  builder: WrenchScrewdriverIcon,
  calendar: CalendarDaysIcon,
  call: PhoneIcon,
  phone: PhoneIcon, // Alias for call
  chat: ChatBubble,
  checklist: ClipboardDocumentListIcon,
  'coach-insights': DocStarIn,
  'coach-notes': ChatBubbleBottomCenterTextIcon,
  'coach-services': UserIcon,
  'coach-services-circle': UserCircleIcon,
  completed: CheckIcon,
  dashboard: Squares2X2Icon,
  document: DocumentTextIcon,
  download: ArrowDownTrayIcon,
  dollar: BanknotesIcon,
  email: EnvelopeIcon,
  entrepreneurship: BuildingStorefrontIcon,
  exercise: Bicycle,
  eye: EyeIcon,
  gym: Bicycle,
  heart: HeartIcon,
  interview: ChatBubbleLeftRightIcon,
  'interview-deloitte': UsersIcon,
  'job-targeting': BriefcaseIcon,
  key: KeyIcon,
  linkedin: LinkedInIcon,
  list: TableCellsIcon,
  negotiation: CoinsSwap,
  network: UserGroupIcon,
  networking: UserGroupIcon,
  'onboarding-success': RocketLaunchIcon,
  orientation: MapIcon,
  pencil: SparklesIcon,
  people: UserGroupIcon,
  'personal-brand': SparklesIcon,
  quiz: PuzzlePieceIcon,
  recruiting: BriefcaseIcon,
  'related-occupations': BuildingOffice2Icon,
  research: BookOpenIcon,
  retirement: TrophyIcon,
  return: ArrowUturnRightIcon,
  'return-left': ArrowUturnLeftIcon,
  salary: BanknotesIcon,
  'sample-doc': DocumentTextIcon,
  scripts: QuoteMessage,
  'search-tools': AreaSearch,
  settings: Cog6ToothIcon,
  'short-course': Golf,
  skills: AcademicCapIcon,
  star: StarIcon,
  'star-solid': StarSolidIcon,
  tag: TagIcon,
  tasks: RectangleStackIcon,
  target: BriefcaseIcon,
  'tech-skills': Square3Stack3DIcon,
  tips: LightBulbIcon,
  tools: WrenchIcon,
  trash: TrashIcon,
  video: VideoCameraIcon,
  webinar: ComputerDesktopIcon,
  wellness: Donate,
  x: XMarkIcon,
  'ellipsis-horizontal': EllipsisHorizontalIcon,
  filter: FunnelIcon,
  refresh: ArrowPathIcon,
  rocket: RocketLaunchIcon, // Alias for onboarding-success
  briefcase: BriefcaseIcon, // Alias for job-targeting
}

// Export individual icon components
export const ActionPlan = Strategy
export const Add = PlusIcon
export const Ayla = MagicWand
export const Article = Compass
export const Assessment = BrainResearch
export const Audio = SpeakerWaveIcon
export const Bookmark = BookmarkIcon
export const Builder = WrenchScrewdriverIcon
export const Calendar = CalendarDaysIcon
export const Call = PhoneIcon
export const Chat = ChatBubble
export const Checklist = ClipboardDocumentListIcon
export const CoachInsights = DocStarIn
export const CoachNotes = ChatBubbleBottomCenterTextIcon
export const CoachServices = UserIcon
export const CoachServicesCircle = UserCircleIcon
export const Completed = CheckIcon
export const Dashboard = Squares2X2Icon
export const Download = ArrowDownTrayIcon
export const Email = EnvelopeIcon
export const Entrepreneurship = BuildingStorefrontIcon
export const Exercise = Bicycle
export const Eye = EyeIcon
export const Heart = HeartIcon
export const Interview = ChatBubbleLeftRightIcon
export const InterviewDeloitte = UsersIcon
export const JobTargeting = BriefcaseIcon
export const List = TableCellsIcon
export const Negotiation = CoinsSwap
export const Networking = UserGroupIcon
export const OnboardingSuccess = RocketLaunchIcon
export const Orientation = MapIcon
export const PersonalBrand = SparklesIcon
export const Quiz = PuzzlePieceIcon
export const Research = BookOpenIcon
export const Retirement = TrophyIcon
export const Return = ArrowUturnRightIcon
export const ReturnLeft = ArrowUturnLeftIcon
export const SampleDoc = DocumentTextIcon
export const Scripts = QuoteMessage
export const SearchTools = AreaSearch
export const Settings = Cog6ToothIcon
export const ShortCourse = Golf
export const Skills = AcademicCapIcon
export const Tag = TagIcon
export const Tips = LightBulbIcon
export const Tools = WrenchIcon
export const Trash = TrashIcon
export const Video = VideoCameraIcon
export const Webinar = ComputerDesktopIcon
export const Wellness = Donate
export const X = XMarkIcon

function YnsIcon({ name, className = '', size = 24, ...props }) {
  // Convert name to lowercase and handle special characters
  const normalizedName = name.toLowerCase().replace(/\s+/g, '-')
  const IconComponent = iconMap[normalizedName]

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in icon map`)
    return null
  }

  // Check if className already includes width/height classes
  const hasWidthClass = /\bw-\d+\b/.test(className)
  const hasHeightClass = /\bh-\d+\b/.test(className)

  // Add size classes only if they're not already present
  const sizeClasses =
    !hasWidthClass && !hasHeightClass ? `w-[${size}px] h-[${size}px]` : ''
  const finalClassName = `${className} ${sizeClasses}`.trim()

  return <IconComponent className={finalClassName} {...props} />
}

export default YnsIcon
